import React, { useEffect, useMemo, useState } from "react"
import { Button, Checkbox, FormControl, InputAdornment, InputLabel, ListSubheader, MenuItem, Select } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { makeStyles } from "@mui/styles"
import UnhencedTextField from "./UnhencedTextField"

const useStyles = makeStyles(() => ({
  actionContainer: {
    padding: [[6, 9]],
    display: "flex",
    justifyContent: "space-between"
  },
  actionButton: {
    padding: 0,
    height: 20,
    minWidth: null,
    background: "none", // Remove background color
    boxShadow: "none", // Remove box shadow
    textTransform: "none", // Prevent text from transforming to uppercase
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "underline", // Underline text on hover
    },
  },
  listHead: {
    lineHeight: null
  }
}))

const UnhencedSelect = ({ onChange, options = [], multiple = false, value, withSearch = false, reset, label, render, ...rest }) => {
  const [searchText, setSearchText] = useState("")
  const [inputValue, setInputValue] = useState(value)

  const classes = useStyles()

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleChange = (e) => {
    setInputValue(e.target.value)
    onChange && onChange(e)
  }

  const displayedOptions = useMemo(
    () => options.filter(option => option.type === "subheader" || String(option.label).toLowerCase().indexOf(searchText.toLowerCase()) > -1),
    [searchText, options]
  )

  // Without a custom render, display the option label (ex. "Prestation Externe 1"), not its raw value (ex. "EXTERNAL")
  const renderItem = (item) => {
    if (render) return render(item)
    const option = options.find(option => option.type !== "subheader" && option.value === item)
    return option?.label ?? item
  }

  const extraProps = {}

  if (withSearch) {
    extraProps.MenuProps = { autoFocus: false }
    extraProps.onClose = () => setSearchText("")
  }

  const _selectAll = () => {
    const allValues = displayedOptions.filter(o => o.type !== "subheader").map(option => option.value)
    setInputValue(allValues)
    onChange && onChange({ target: { value: allValues } })
  }

  if (multiple && !Array.isArray(inputValue)) {
    return null
  }

  return (
    <SelectWrapper label={label} classes={classes}>
      <Select
        {...rest}
        {...extraProps}
        onChange={handleChange}
        value={inputValue}
        multiple={multiple}
        renderValue={(selected) => multiple ? selected.map(item => renderItem(item)).join(", ") : renderItem(selected)}
      >
        <ListSubheader>
          {
            options.length > 20 &&
            <div className={classes.actionContainer}>
              <Button className={classes.actionButton} onClick={reset}>Effacer</Button>
              {
                multiple ?
                  <Button className={classes.actionButton} onClick={_selectAll}>Tout sélectionner</Button>
                  : null
              }
            </div>
          }
          {
            withSearch ?
              <UnhencedTextField
                variant="outlined"
                size="small"
                autoFocus
                placeholder="Rechercher"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") {
                    // Prevents autoselecting item while typing (default Select behaviour)
                    e.stopPropagation()
                  }
                }}
              />
              : null
          }
        </ListSubheader >
        {
          displayedOptions.map((option, idx) => {
            if (option.type === "subheader") {
              return <ListSubheader key={option.label + idx}>{option.label}</ListSubheader>
            }
            const iconEl = option.icon
              ? <span style={{ display: "inline-flex", alignItems: "center", marginRight: 8, flexShrink: 0 }}>{option.icon}</span>
              : null
            return multiple ? (
              <MenuItem key={option.key} value={option.value}>
                <Checkbox
                  checked={Array.isArray(inputValue) ? inputValue.findIndex(selectedOption => option.value === selectedOption) > -1 : false}
                  disableRipple
                  style={{ padding: 4, marginRight: 6 }}
                />
                {iconEl}
                {option.label}
              </MenuItem>
            ) : (
              <MenuItem key={option.key} value={option.value} sx={{ display: "flex", alignItems: "center" }}>
                {iconEl}
                {option.label}
              </MenuItem>
            )
          })
        }
        {
          (options.length < 20) &&
          <div className={classes.actionContainer}>
            <Button className={classes.actionButton} onClick={reset}>Effacer</Button>
            {
              multiple ?
                <Button className={classes.actionButton} onClick={_selectAll}>Tout sélectionner</Button>
                : null
            }
          </div>
        }
      </Select>
    </SelectWrapper>
  )
}

export default UnhencedSelect

const SelectWrapper = ({ label, children }) => {
  return label
    ? (
      <FormControl fullWidth>
        <InputLabel htmlFor={label} variant="standard">
          {label}
        </InputLabel>
        {children}
      </FormControl>
    )
    : <>{children}</>
}